import { inject, Injectable, OnDestroy } from '@angular/core';
import type { AdditionalInfoFieldKey } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { AdditionalInfoResult } from '@modules/rx-intuitive-crown-additional-info/models/additional-info';
import { CrownForm } from '@modules/rx-intuitive-crown/models/crown-form';
import { getNewTooth, Tooth } from '@modules/teeth-diagram/models/tooth';
import { UnitTypes } from '@modules/teeth-diagram/models/unit-type.enum';
import { IdName } from '@shared/models/id-name';
import { PonticDesignEnum } from '@shared/models/pontic-design.enum';
import { requireNonNull } from '@shared/utils/type-util';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { ValidateRxService } from '@shared/services/validate-rx';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { RxIntuitiveCopyInBridgeService } from './services/rx-intuitive-copy-in-bridge.service';
import { RxIntuitiveBridgeValidationService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-bridge-validation.service';
import { IntuitiveBridge } from '@modules/rx-intuitive-teeth-orchestrator/models/intuitive-bridge';
import { IntuitiveToothForTreatment } from '@modules/rx-intuitive-teeth-orchestrator/models';
import { IntuitiveIndication } from './models/intuitive-indication';
import { ToothModalResult } from './models/tooth-modal';
import { TeethNumberingSystem } from './models';
import { RxIntuitiveTeethHelperService } from './services/rx-intuitive-teeth-helper.service';
import { RxIntuitiveToothEditorRoter } from './services/rx-intuitive-tooth-editor-roter.service';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';
import { RxIntuitiveBridgeService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-bridge-service';
import { RxIntuitiveTeethDiagramStore } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-store';
import { TeethDiagramStore } from '@modules/teeth-diagram/state/teeth-diagram-store';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { RxIntuitiveTeethDiagramQuery } from '@modules/rx-intuitive-teeth-orchestrator/state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveIndicationRulesService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-rules.service';
import { LoggerService } from '@core/services/logger/logger.service';
import { RxIntuitiveToothEditorReadOnlyService } from './services/tooth-editor/rx-intuitive-tooth-editor-read-only.service';
import { RxIntuitiveToothEditorMutationService } from './services/tooth-editor/rx-intuitive-tooth-editor-mutation.service';
import { RxIntuitiveToothEditorViewService } from './services/tooth-editor/rx-intuitive-tooth-editor-view.service';
import { RxIntuitiveToothTreatmentMapper } from './services/rx-intuitive-tooth-treatment-mapper.service';
import { IRxIntuitiveToothEditorFacade } from './models/rx-intuitive-tooth-editor-interface.facade';

@Injectable()
export class RxIntuitiveToothEditorBridgeFacade implements IRxIntuitiveToothEditorFacade, OnDestroy {
	readonly facadeName: string = 'RxIntuitiveToothEditorBridgeFacade';

	// ── Bridge-only injections ────────────────────────────────────────────────
	private readonly sharingService = inject(RxIntuitiveCopyInBridgeService);
	private readonly validationService = inject(RxIntuitiveBridgeValidationService);
	private readonly validateRxService = inject(ValidateRxService);

	// ── Read-only / permission (delegated) ────────────────────────────────────
	readonly toothEditorReadOnly$: Observable<boolean> = this.readOnlyService.toothEditorReadOnly$;
	readonly canEditToothEditor$: Observable<boolean> = this.readOnlyService.canEditToothEditor$;

	/** Delete control — off in shell read-only (same gate as canEditToothEditor$). */
	readonly canDeleteToothEditor$: Observable<boolean> = combineLatest([
		this.viewService.isToothEditorShown$,
		this.readOnlyService.toothEditorReadOnly$
	]).pipe(map(([editorShown, readOnly]) => editorShown && !readOnly));

	// ── View / query (delegated) ──────────────────────────────────────────────
	readonly teethNumberingSystem$ = this.viewService.teethNumberingSystem$;
	readonly isToothEditorShown$: Observable<boolean> = this.viewService.isToothEditorShown$;
	readonly teethFlat$ = this.viewService.teethFlat$;
	readonly editorTeeth$: Observable<IntuitiveToothForTreatment[]> = this.viewService.editorTeeth$;
	readonly previewTreatmentListItems$ = this.viewService.previewTreatmentListItems$;

	// ── Bridge-only observables ───────────────────────────────────────────────
	readonly selectedBridge$: Observable<IntuitiveBridge> = this.teethHelper.selectedBridgeNonNull$;

	readonly hasUnsavedChanges$: Observable<boolean> = combineLatest([
		this.selectedBridge$,
		this.teethDiagramQuery.teethFlat$
	]).pipe(
		map(([selectedBridge, teethFlat]) => {
			const originalBridgeState = this.bridgeService.getBridgeSpanByBridgeIndex(selectedBridge.index, teethFlat);

			return originalBridgeState ? this.bridgeService.hasUnsavedChanges(selectedBridge, originalBridgeState) : false;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly selectedBridgeTeeth$: Observable<Tooth[]> = this.intuitiveQuery.selectedBridgeTeeth$;

	readonly bridgeEditorTeeth$: Observable<IntuitiveToothForTreatment[]> = combineLatest([
		this.selectedBridgeTeeth$,
		this.intuitiveQuery.selectedToothIds$,
		this.teethHelper.teethNumberingSystem$,
		this.intuitiveQuery.toothLastTouchedAt$,
		this.teethHelper.selectedBridgeNonNull$
	]).pipe(
		map(
			([bridgeTeeth, selectedToothIds, teethNumberingSystem, toothLastTouchedAt, bridge]: [
				Tooth[],
				number[],
				TeethNumberingSystem,
				Record<number, number>,
				IntuitiveBridge
			]) => {
				void bridge;
				const selected = bridgeTeeth.filter(t => t.ToothID !== null && selectedToothIds.includes(t.ToothID));

				return selected
					.map(t => this.toBridgeToothForTreatment(t, teethNumberingSystem, toothLastTouchedAt))
					.sort((a, b) => b.lastTouchedAt - a.lastTouchedAt || a.toothId - b.toothId);
			}
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly canEditBridgeToothEditor$: Observable<boolean> = combineLatest([
		this.bridgeEditorTeeth$.pipe(map(editorTeeth => this.viewService.computeCanEditToothEditor(editorTeeth))),
		this.readOnlyService.toothEditorReadOnly$
	]).pipe(map(([canEditByUnitType, readOnly]) => canEditByUnitType && !readOnly));

	readonly ponticDesigns$: Observable<IdName[]> = this.shellQuery.ponticDesigns$;

	readonly bridgeListItem$ = combineLatest([
		this.selectedBridge$,
		this.teethHelper.teethNumberingSystem$,
		this.intuitiveQuery.toothLastTouchedAt$
	]).pipe(
		map(([bridge, teethNumberingSystem, toothLastTouchedAt]) =>
			this.teethHelper.mapBridgeToDiagramListItem(bridge, teethNumberingSystem, toothLastTouchedAt)
		),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	// ── Lifecycle ─────────────────────────────────────────────────────────────
	readonly facadeDestroy$ = new Subject<void>();

	constructor(
		protected readonly readOnlyService: RxIntuitiveToothEditorReadOnlyService,
		protected readonly mutationService: RxIntuitiveToothEditorMutationService,
		protected readonly viewService: RxIntuitiveToothEditorViewService,
		protected readonly teethHelper: RxIntuitiveTeethHelperService,
		protected readonly editorRouting: RxIntuitiveToothEditorRoter,
		protected readonly indicationMapper: RxIntuitiveIndicationMapperService,
		protected readonly bridgeService: RxIntuitiveBridgeService,
		protected readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		protected readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		protected readonly teethDiagramStore: TeethDiagramStore,
		protected readonly teethDiagramQuery: TeethDiagramQuery,
		protected readonly indicationsService: RxIntuitiveIndicationRulesService,
		protected readonly shellQuery: ShellQuery,
		protected readonly logger: LoggerService,
		protected readonly toothTreatmentMapper: RxIntuitiveToothTreatmentMapper
	) {}

	ngOnDestroy(): void {
		this.facadeDestroy$.next();
		this.facadeDestroy$.complete();
	}

	// ── Shared interface (shouldValidateForSend — bridge override) ────────────

	/** Bridge facade reads directly from shellQuery, bypassing the helper. */
	get shouldValidateForSend(): boolean {
		return this.shellQuery.shouldValidateForSend;
	}

	// ── Shared interface mutations (delegated) ────────────────────────────────

	resetIndicationForSelectedTeeth(): void {
		const teeth = this.teethDiagramQuery.teethRecord;
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;
		const toothIdsToReset = selectedToothIds.filter(id => !this.indicationMapper.isDefaultUnitType(teeth[id]?.UnitTypeID));

		this.mutationService.resetIndicationForTeeth(toothIdsToReset);

		if (toothIdsToReset.length > 0) {
			this.updateSelectedToothIds([]);
		}
	}

	resetIndicationForTeeth(toothIdsToReset: number[]): void {
		this.mutationService.resetIndicationForTeeth(toothIdsToReset);
	}

	updateToothCrownData(crownForm: CrownForm): void {
		this.mutationService.updateToothCrownData(crownForm);
	}

	applyToothAdditionalInfo(toothIds: number[], result: AdditionalInfoResult): void {
		this.mutationService.applyToothAdditionalInfo(toothIds, result);
	}

	clearDisabledToothFields(toothIds: number[], payload: Partial<Record<string, unknown>>): void {
		this.mutationService.clearDisabledToothFields(toothIds, payload);
	}

	hasAdditionalInfoButtonSaved(teethFlat: readonly Tooth[], sessionToothIds: readonly number[]): boolean {
		return this.viewService.hasAdditionalInfoButtonSaved(teethFlat, sessionToothIds);
	}

	selectBridge(bridge: IntuitiveBridge, focusOnTooth?: number): void {
		this.intuitiveStore.selectBridge(bridge, focusOnTooth);
	}

	deselectBridge(): void {
		this.intuitiveStore.deselectBridge();
	}

	shouldShowToothEditorProperties(indicationId: number): boolean {
		return this.indicationsService.shouldShowToothEditorProperties({ indicationId });
	}

	openTreatmentEditorFromList(toothId: number): void {
		const teethRecord = this.teethHelper.teethRecord;
		const tooth = this.teethHelper.getToothById(toothId, teethRecord);

		if (tooth?.BridgeIndex) {
			const bridge = this.bridgeService.getBridgeSpanByBridgeIndex(tooth.BridgeIndex, this.teethDiagramQuery.teethFlat);

			if (bridge) {
				return this.selectBridge(bridge, toothId);
			}
		}

		if (tooth?.UnitTypeID) {
			this.updateSelectedToothIds([toothId]);

			const modalResult$ = this.openModalForUnitTypeIfNeeded(
				this.indicationMapper.getUnitTypeIndicationForDiagram(tooth.UnitTypeID)
			);

			if (modalResult$) {
				modalResult$.pipe(takeUntil(this.facadeDestroy$)).subscribe();
			}
		}
	}

	// ── Bridge-specific modal routing ─────────────────────────────────────────

	/**
	 * Opens the appropriate bridge tooth modal (BridgeImplantBased).
	 * Returns null if no modal is needed for this indication.
	 */
	openModalForUnitTypeIfNeeded(indication: IntuitiveIndication): Observable<ToothModalResult> | null {
		return this.editorRouting.openBridgeModalForIndicationIfNeeded(indication, {
			onSave: (toothIds: number[], partialUpdate: Partial<Tooth>) => {
				this.updateBridgeTeethByIdsForIndicationWithModal(toothIds, partialUpdate);
			},
			onDelete: (toothIds: number[]) => {
				this.resetBridgeTeethToDefaultUnitType(toothIds);
			},
			onClearedFields: (toothIds: number[], clearedFields: string[]) => {
				this.mutationService.markCrownControlsAsRemovedByUser(toothIds, clearedFields as AdditionalInfoFieldKey[]);
			}
		});
	}

	// ── Bridge-only mutations ─────────────────────────────────────────────────

	updateBridgeTeethCrownData(crownForm: CrownForm): void {
		const partialToothUpdate = {
			MaterialID: crownForm.material,
			ShadeSystemId: crownForm.shadeSystem,
			ShadeBody: crownForm.shadeBody,
			SpecificationId: crownForm.specification
		};

		this.updateBridgeTeethByIds(crownForm.teethIds, partialToothUpdate);

		if (this.intuitiveQuery.selectedBridge) {
			this.sharingService.updateSharedDefaults(crownForm, this.intuitiveQuery.selectedBridge);
			this.shareCrownDataForBridgeTeeth();
		}
	}

	updateBridgeTeethPonticDesign(toothIds: number[], ponticDesignId: PonticDesignEnum | null): void {
		this.updateBridgeTeethByIds(toothIds, { PonticDesignID: ponticDesignId });
		this.shareCrownDataForBridgeTeeth();
	}

	updateBridgeTeethByIds(toothIds: number[], partialUpdate: Partial<Tooth>): void {
		this.intuitiveStore.updateBridgeTeethByIds(toothIds, partialUpdate);
		this.mutationService.markRecentlyEditedTeeth(toothIds);
	}

	deleteBridge(): void {
		const currentBridge = requireNonNull(this.intuitiveQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		if (originalBridge) {
			const resetTeethIds = originalBridge.teeth
				.filter((tooth): tooth is Tooth & { ToothID: number } => tooth.ToothID !== null)
				.map(tooth => tooth.ToothID);

			this.teethDiagramStore.resetIndicationByIds(resetTeethIds, UnitTypes.Regular);
		}

		const bridgeToothIds = currentBridge.teeth.filter(t => t.ToothID != null).map(t => t.ToothID) as number[];

		this.intuitiveStore.clearRecentlyEditedTeeth(bridgeToothIds);
		this.sharingService.clearSharedDefaults(currentBridge);
		this.deselectBridge();
	}

	saveBridge(): void {
		const currentBridge = requireNonNull(this.intuitiveQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		this.intuitiveStore.setBridgeSubmitted();

		const error = this.validationService.validateBridge(currentBridge);

		if (error.summary.length > 0 || Object.values(error.teeth).some(errors => errors.length > 0)) {
			return;
		}

		if (originalBridge) {
			const persistedTeeth = originalBridge.teeth.filter(t => t.ToothID != null).map(t => t.ToothID) as number[];

			this.teethDiagramStore.resetIndicationByIds(persistedTeeth, UnitTypes.Regular);
		}

		this.teethDiagramStore.updateTeeth({ teethToUpdate: currentBridge.teeth });
		this.deselectBridge();
	}

	revertChanges(): void {
		const currentBridge = requireNonNull(this.intuitiveQuery.selectedBridge);
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		if (originalBridge != null) {
			return this.selectBridge(originalBridge);
		}
	}

	closeBridge(): void {
		this.deselectBridge();
	}

	forceSaveBridge(): void {
		const currentBridge = this.intuitiveQuery.selectedBridge;

		if (!currentBridge) {
			return;
		}

		const hasAnyMissingIndication = currentBridge.teeth.some(t => this.validationService.isDefaultBridgeIndication(t));
		const originalBridge = this.bridgeService.getBridgeSpanByBridgeIndex(currentBridge.index, this.teethDiagramQuery.teethFlat);

		// Always reset all current bridge teeth first to clear any unsaved state
		const allCurrentTeethIds = currentBridge.teeth.filter(t => t.ToothID != null).map(t => t.ToothID) as number[];

		this.teethDiagramStore.resetIndicationByIds(allCurrentTeethIds, UnitTypes.Regular);

		if (!hasAnyMissingIndication) {
			// All teeth have indications — commit the full current bridge
			this.teethDiagramStore.updateTeeth({ teethToUpdate: currentBridge.teeth });
		} else if (originalBridge) {
			// Some teeth missing indication but original bridge exists — restore saved state
			this.teethDiagramStore.updateTeeth({ teethToUpdate: originalBridge.teeth });
		}
		// else: brand-new bridge with missing indications — discard entirely (already reset)

		this.intuitiveStore.deselectBridge();
	}

	// ── Private helpers ───────────────────────────────────────────────────────

	private shareCrownDataForBridgeTeeth(): void {
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;

		if (!selectedToothIds || selectedToothIds.length === 0) {
			this.logger.warn('change crown data called with no teeth selected', { module: this.facadeName });

			return;
		}

		const selectedTooth = this.intuitiveQuery.selectedBridgeTeeth.find(t => t.ToothID === selectedToothIds[0]);

		if (!selectedTooth) {
			this.logger.warn('change crown data called with no teeth selected', { module: this.facadeName });

			return;
		}

		const targetTeeth = this.intuitiveQuery.selectedBridgeTeeth.filter(t => t.ToothInBridgeTypeID !== null);
		const updates = this.sharingService.shareDefaulPropsToBridgeTeeth(targetTeeth, selectedTooth);

		for (const { toothId, partialToUpdate } of updates) {
			this.intuitiveStore.updateBridgeTeethByIds([toothId], partialToUpdate);
		}

		this.mutationService.markRecentlyEditedTeeth(updates.map(u => u.toothId));
	}

	private updateBridgeTeethByIdsForIndicationWithModal(toothIds: number[], partialToothUpdate: Partial<Tooth>): void {
		const firstTooth = this.intuitiveQuery.selectedBridgeTeeth.find(toothInBridge =>
			toothIds.includes(toothInBridge.ToothID as number)
		);

		if (!firstTooth) {
			return;
		}

		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { ToothID: toothId, ...resetFields } = getNewTooth({
			...partialToothUpdate,
			UnitTypeID: firstTooth.UnitTypeID,
			BridgeIndex: firstTooth.BridgeIndex,
			ToothInBridgeTypeID: firstTooth.ToothInBridgeTypeID
		});

		this.updateBridgeTeethByIds(toothIds, resetFields);
	}

	private resetBridgeTeethToDefaultUnitType(toothIds: number[]): void {
		if (toothIds.length === 0) {
			return;
		}

		const firstTooth = this.intuitiveQuery.selectedBridgeTeeth.find(toothInBridge =>
			toothIds.includes(toothInBridge.ToothID as number)
		);

		if (!firstTooth) {
			return;
		}

		const defaultUnitType = this.indicationMapper.getDefaultUnitType();

		// eslint-disable-next-line @typescript-eslint/no-unused-vars
		const { ToothID: toothId, ...resetFields } = getNewTooth({
			UnitTypeID: defaultUnitType,
			BridgeIndex: firstTooth.BridgeIndex
		});

		this.updateBridgeTeethByIds(toothIds, resetFields);
	}

	private toBridgeToothForTreatment(
		tooth: Tooth,
		teethNumberingSystem: TeethNumberingSystem,
		toothLastTouchedAt: Record<number, number>
	): IntuitiveToothForTreatment {
		const mapped = this.toothTreatmentMapper.toIntuitiveToothForTreatment(tooth, teethNumberingSystem, toothLastTouchedAt, true);

		return { ...mapped, isInvalid: this.validateToothForSend(tooth) };
	}

	private validateToothForSend(tooth: Tooth): boolean {
		if (!this.shouldValidateForSend) {
			return false;
		}

		const toothIndication = this.indicationMapper.getBridgeIndicationForDiagram(tooth);
		const isDefaultIndication = this.indicationMapper.isDefaultIndication(toothIndication);

		return isDefaultIndication ? false : this.validateRxService.isTreatmentInvalidForSend(tooth);
	}

	private updateSelectedToothIds(toothIds: number[]): void {
		this.intuitiveStore.update({ selectedToothIds: toothIds });
	}
}
